import { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2, AlertTriangle, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { Mono, money } from "./ui";

const FLAG_LABEL = { pre_event: "pre-event expenditure", out_of_window: "outside works window", duplicate: "duplicate invoice",
  indirect: "indirect in direct works", day_labour_not_opt_in: "day labour — no opt-in", before_opt_in: "before opt-in date",
  payroll_tax: "state payroll tax", manual_journal: "manual journal / accrual", capital_purchase: "capital purchase",
  ineligible_role: "ineligible personnel", plant_overhead: "plant overhead" };

export default function LodgementModal({ claimId, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/fieldclaim/claims/${claimId}/lodgement-check`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [claimId]);

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-navy-900 border border-gold-500/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lodgement-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold-500/20 sticky top-0 bg-navy-900 z-10">
          <div>
            <Mono className="text-gold-400 text-[0.55rem]">Lodgement readiness</Mono>
            <div className="font-heading text-lg">Assessed before it leaves the building</div>
          </div>
          <button onClick={onClose} data-testid="lodgement-close"><X className="h-5 w-5 text-white/60" /></button>
        </div>

        {!data ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold-400" /></div>
        ) : data.error ? (
          <div className="p-8 text-red-300 text-sm">Could not run the lodgement checks. Try again.</div>
        ) : (
          <div className="p-6 space-y-5">
            <div className={`border px-4 py-3 flex items-center gap-3 ${data.ready ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10"}`} data-testid="lodgement-verdict">
              {data.ready ? <CheckCircle2 className="h-5 w-5 text-emerald-300 shrink-0" /> : <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0" />}
              <div className="text-sm">
                <div className={data.ready ? "text-emerald-300" : "text-amber-300"}>
                  {data.ready ? "Ready to lodge — every assessment test passes." : "Not ready — items below will be picked up at assessment."}
                </div>
                <div className="text-white/45 text-xs mt-0.5">{data.note}</div>
              </div>
            </div>

            {data.windows?.length > 0 && (
              <div className="border border-gold-500/20 p-4">
                <Mono className="text-[0.5rem] text-white/50 block mb-2">Completion windows on this claim</Mono>
                <div className="flex flex-wrap gap-4">
                  {data.windows.map((w) => (
                    <div key={w.measure} className="text-sm" data-testid={`window-${w.measure}`}>
                      <span className="font-mono text-gold-300">{w.measure}</span>
                      <span className="text-white/50 text-xs ml-2">to {w.window_end || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Mono className="text-[0.5rem] text-gold-400/80 block">Assessment tests</Mono>
              {data.checks.map((c) => (
                <div key={c.code} className={`border p-3 ${c.severity === "pass" ? "border-white/10" : "border-amber-500/40 bg-amber-500/5"}`} data-testid={`lodgement-check-${c.code}`}>
                  <div className="flex items-center gap-2">
                    {c.severity === "pass" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-300 shrink-0" />}
                    <span className="text-sm text-white">{c.label}</span>
                    {c.amount ? <span className="ml-auto font-mono text-xs text-amber-300">{money(c.amount)}</span> : null}
                  </div>
                  <p className={`text-xs mt-1 ml-5.5 ${c.severity === "pass" ? "text-white/35" : "text-white/65"}`}>{c.detail}</p>
                </div>
              ))}
            </div>

            {Object.keys(data.ledger_flags || {}).length > 0 && (
              <div className="border border-amber-500/30 p-4" data-testid="lodgement-ledger-flags">
                <Mono className="text-[0.5rem] text-amber-300/80 block mb-2 flex items-center gap-1.5"><ClipboardCheck className="h-3 w-3" /> Ledger lines needing a ruling ({data.open_flagged_lines} open)</Mono>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.ledger_flags).map(([f, n]) => (
                    <span key={f} className="border border-amber-400/40 text-amber-300 text-[0.6rem] font-mono px-2 py-1">{FLAG_LABEL[f] || f} × {n}</span>
                  ))}
                </div>
                <p className="text-white/45 text-xs mt-2.5">Rule each of these in Assess → Ledger finalisation before lodging.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
